import type {
  AndamentoProcesso,
  Especialista,
  FaseProcesso,
  MensagemChatItem,
  ProcessoArbitralDetail,
  ProcessoArbitralDetailCru,
  ResponsavelFase,
  SituacaoAtiva,
  TipoEventoAndamento,
} from '../types/arbitralDetailTypes';
import { normalizeArbitralDetailStatus } from './arbitralDetailUtils';

export async function parseErrorMessage(response: Response, fallback: string): Promise<string> {
  const errorBody = await response.json().catch(() => ({}));
  return (errorBody as { error?: { message?: string } }).error?.message ?? fallback;
}

function normalizeEspecialista(raw: Record<string, unknown>): Especialista {
  return {
    id: String(raw.id ?? ''),
    nome: String(raw.nome ?? ''),
    papel: 'ARBITRO',
    cargo: typeof raw.papel === 'string' ? raw.papel : undefined,
    // Ausente (core sem o campo, janela de deploy) esconde o botão: oferecer uma impugnação que o
    // servidor pode recusar é pior que esperar o deploy do core.
    impugnacaoNoPrazo: raw.impugnacaoNoPrazo === true,
  };
}

export function normalizeSituacao(raw: Record<string, unknown>): SituacaoAtiva {
  return {
    id: String(raw.id ?? ''),
    key: String(raw.key ?? ''),
    label: String(raw.label ?? ''),
    statusPai: typeof raw.statusPai === 'string' ? raw.statusPai : null,
  };
}

export function normalizeAndamento(raw: Record<string, unknown>): AndamentoProcesso {
  const tipo = typeof raw.tipo === 'string' ? (raw.tipo as TipoEventoAndamento) : 'MANUAL';

  return {
    id: String(raw.id ?? ''),
    dataHora: String(raw.dataHora ?? raw.dataCriacao ?? new Date().toISOString()),
    tipo,
    descricao: String(raw.descricao ?? ''),
    autor: String(raw.autor ?? 'Sistema'),
    autorId: typeof raw.autorId === 'string' ? raw.autorId : null,
    papel: typeof raw.papel === 'string' ? (raw.papel as AndamentoProcesso['papel']) : undefined,
    responsaveis: Array.isArray(raw.responsaveis) ? (raw.responsaveis as string[]) : undefined,
    prazoFinal: typeof raw.prazoFinal === 'string' ? raw.prazoFinal : undefined,
    isContestacao: raw.isContestacao === true ? true : undefined,
    anexos: Array.isArray(raw.anexos) ? (raw.anexos as AndamentoProcesso['anexos']) : undefined,
  };
}

function normalizeFase(raw: Record<string, unknown>): FaseProcesso {
  return {
    id: String(raw.id ?? ''),
    nome: String(raw.nome ?? ''),
    status: typeof raw.status === 'string' ? (raw.status as FaseProcesso['status']) : 'AGUARDANDO',
    dataInicio: typeof raw.dataInicio === 'string' ? raw.dataInicio : undefined,
    dataConclusao: typeof raw.dataConclusao === 'string' ? raw.dataConclusao : undefined,
    executor: typeof raw.executor === 'string' ? raw.executor : undefined,
    prazoVigente: typeof raw.prazoVigente === 'string' ? raw.prazoVigente : undefined,
    dataVencimentoPrazo:
      typeof raw.dataVencimentoPrazo === 'string' ? raw.dataVencimentoPrazo : undefined,
    responsavelAtual:
      raw.responsavelAtual === null
        ? null
        : typeof raw.responsavelAtual === 'string'
          ? (raw.responsavelAtual as ResponsavelFase)
          : undefined,
  };
}

export function normalizeProcesso(raw: ProcessoArbitralDetailCru): ProcessoArbitralDetail {
  const rawAndamentos = (raw.andamentos ?? []) as unknown as Record<string, unknown>[];
  const rawFases = (raw.fases ?? []) as unknown as Record<string, unknown>[];
  const rawEspecialistas = (raw.especialistas ?? []) as unknown as Record<string, unknown>[];
  return {
    ...raw,
    status: normalizeArbitralDetailStatus(raw.status as unknown as string),
    partes: raw.partes,
    andamentos: rawAndamentos.map(normalizeAndamento),
    fases: rawFases.map(normalizeFase),
    situacoes: raw.situacoes ?? [],
    especialistas: rawEspecialistas.map(normalizeEspecialista),
    // Backend sem os campos novos (janela de deploy core×Vercel) não pode derrubar a aba:
    // `anexos.length`/`deduplicarRastro(rastro)` com undefined é TypeError no render.
    pendencias: (raw.pendencias ?? []).map((p) => ({
      ...p,
      titulo: typeof p.titulo === 'string' ? p.titulo : '',
      anexos: Array.isArray(p.anexos) ? p.anexos : [],
      rastro: Array.isArray(p.rastro) ? p.rastro : [],
    })),
  };
}

export function normalizeMensagem(raw: Record<string, unknown>): MensagemChatItem {
  return {
    id: String(raw.id ?? ''),
    dataHora: String(raw.dataHora ?? new Date().toISOString()),
    tipo: (raw.tipo as MensagemChatItem['tipo']) ?? 'TEXTO',
    texto: String(raw.texto ?? ''),
    autorNome: String(raw.autorNome ?? ''),
    autorTipo: (raw.autorTipo as MensagemChatItem['autorTipo']) ?? 'SISTEMA',
    autorUsuarioId: String(raw.autorUsuarioId ?? ''),
    lida: raw.lida === true,
    arquivosAnexados: Array.isArray(raw.arquivosAnexados)
      ? (raw.arquivosAnexados as Record<string, unknown>[]).map((a) => ({
          id: String(a.id ?? ''),
          nome: String(a.nome ?? ''),
          url: String(a.url ?? ''),
          tamanhoBytes: Number(a.tamanhoBytes ?? 0),
          mimeType: String(a.mimeType ?? ''),
        }))
      : [],
  };
}
